import { useEffect, useMemo, useRef, useState } from 'react';
import { Bubble } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Section } from '../../ReportUIPrimitives';
import styles from '../../MetaReportViewer.module.css';
import { PerfField } from './common';
import { fmt } from './commonData';
import { analyzeShopMetaAdset, getShopMetaAdsetAnalysis } from '../../../../utils/api/reportApi';
import { LeakWaterfallChart } from './funnelCharts';
import { useAnalysisFlow, AnalysisFlowLog } from './AnalysisFlowLog';

ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

const formatAudienceType = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(' / ') || '—';
  if (value == null || value === '') return '—';
  return String(value);
};

const formatAnalysisKey = (key = '') => String(key || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatAnalysisValue = (value) => {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    if (value.every((item) => item == null || typeof item !== 'object')) {
      return value.map((item) => String(item)).join(', ');
    }
    return JSON.stringify(value, null, 2);
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const buildKeyValueEntries = (objectValue = {}) =>
  Object.entries(objectValue || {}).filter(([, value]) => value != null && value !== '');

const formatDetailValue = (value) => {
  if (Array.isArray(value)) {
    const cleaned = value.map((item) => String(item || '').trim()).filter(Boolean);
    return cleaned.length ? cleaned.join(', ') : '—';
  }
  return formatAnalysisValue(value);
};

const getThemeChartColors = () => {
  const rootStyle =
    typeof window !== 'undefined'
      ? getComputedStyle(document.documentElement)
      : null;

  return {
    primaryText:
      rootStyle?.getPropertyValue('--tp')?.trim()
      || rootStyle?.getPropertyValue('--text-primary')?.trim()
      || '#f3f4f6',
    secondaryText:
      rootStyle?.getPropertyValue('--ts2')?.trim()
      || rootStyle?.getPropertyValue('--text-secondary')?.trim()
      || '#d1d5db',
  };
};

const audienceMatrixQuadrantPlugin = {
  id: 'audienceMatrixQuadrantPlugin',
  beforeDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales?.x || !scales?.y) return;
    const themeColors = getThemeChartColors();
    const quadrantStroke = 'rgba(100, 149, 237, 0.5)';
    const quadrantText = themeColors.primaryText;
    const x50 = scales.x.getPixelForValue(50);
    const y50 = scales.y.getPixelForValue(50);

    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = quadrantStroke;
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.moveTo(x50, chartArea.top);
    ctx.lineTo(x50, chartArea.bottom);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(chartArea.left, y50);
    ctx.lineTo(chartArea.right, y50);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.fillStyle = quadrantText;
    ctx.font = '13px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Spend Risk', chartArea.left + 15, chartArea.top + 25);
    ctx.fillText('Low-Impact Weak', chartArea.left + 15, chartArea.bottom - 12);
    ctx.textAlign = 'right';
    ctx.fillText('Scale Winners', chartArea.right - 15, chartArea.top + 25);
    ctx.fillText('Hidden Quality', chartArea.right - 15, chartArea.bottom - 12);
    ctx.restore();
  },
};

const audienceMatrixBubbleLabelPlugin = {
  id: 'audienceMatrixBubbleLabelPlugin',
  afterDraw(chart) {
    const { ctx, tooltip } = chart;
    const { primaryText } = getThemeChartColors();

    // Hide all labels while the tooltip is open to prevent canvas text from
    // bleeding through the tooltip card regardless of z-order.
    if (tooltip && tooltip.opacity > 0) return;

    ctx.save();
    ctx.font = 'bold 11px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    chart.data.datasets.forEach((dataset, index) => {
      const meta = chart.getDatasetMeta(index);
      meta.data.forEach((point, pointIndex) => {
        const raw = dataset.data?.[pointIndex] || {};
        const label = raw?.row?.audience_family_role || dataset.label;
        ctx.fillStyle = primaryText;
        ctx.fillText(label, point.x, point.y);
      });
    });
    ctx.restore();
  },
};

const normalizeMatrixMetric = (rows, accessor) => {
  const values = rows.map(accessor).map((value) => Number(value) || 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (!Number.isFinite(min) || !Number.isFinite(max) || max === min) {
    return () => 50;
  }
  return (row) => (((Number(accessor(row)) || 0) - min) / (max - min)) * 100;
};

const getAudienceFamilyBubbleColor = (audienceFamilyRole) => {
  const role = String(audienceFamilyRole || '').trim().toLowerCase();

  if (role === 'prospecting') return '#3b82f6cc';
  if (role === 'website and app retargeting' || role === 'previous engagements' || role === 'retargeting') {
    return '#22c55ecc';
  }
  if (role === 'customer audience' || role === 'customer list') return '#a855f7cc';
  if (role === 'reactivation') return '#f59e0bcc';
  if (role === 'mixed') return '#9ca3afcc';

  return '#9ca3afcc';
};

const buildAudienceMatrixChartModel = (matrix = {}) => {
  const rows = Array.isArray(matrix?.rows) ? matrix.rows : [];
  if (!rows.length) return { points: [] };

  const spendTotal = rows.reduce((sum, row) => sum + (Number(row.spend) || 0), 0);
  const maxSpend = Math.max(...rows.map((row) => Number(row.spend) || 0));
  const roasScore = normalizeMatrixMetric(rows, (row) => row?.roas);
  const purchaseRateScore = normalizeMatrixMetric(rows, (row) => row?.purchase_rate_from_clicks);
  const revenuePerClickScore = normalizeMatrixMetric(rows, (row) => row?.revenue_per_click);

  const points = rows.map((row, index) => {
    const spend = Number(row.spend) || 0;
    const spendShare = spendTotal > 0 ? (spend / spendTotal) * 100 : 0;
    const efficiencyQuality =
      (roasScore(row) * 0.5) +
      (purchaseRateScore(row) * 0.3) +
      (revenuePerClickScore(row) * 0.2);
    // Bubble radius scales with spend: min 10px, max 42px
    const spendRatio = maxSpend > 0 ? spend / maxSpend : 0;
    const radius = Math.max(10, Math.min(42, 10 + spendRatio * 32));

    return {
      x: Number(efficiencyQuality.toFixed(2)),
      y: Number(spendShare.toFixed(2)),
      r: Number(radius.toFixed(2)),
      label: row.adset_name || row.adset_id || `Adset ${index + 1}`,
      color: getAudienceFamilyBubbleColor(row.audience_family_role),
      row,
    };
  });

  return { points };
};

const AudienceEfficiencyMatrixSection = ({
  matrix,
  currency,
  userId,
  authToken,
  reportId,
  shopId,
  onAssessmentSaved,
  onShowCampaignPerformance,
  onSelectedAdsetChange,
}) => {
  const rows = useMemo(() => (Array.isArray(matrix?.rows) ? matrix.rows : []), [matrix]);
  const { points } = useMemo(() => buildAudienceMatrixChartModel(matrix), [matrix]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [fetchedAssessment, setFetchedAssessment] = useState(null);
  const [isAssessmentLoading, setIsAssessmentLoading] = useState(false);
  const lastEmittedSelectedAdsetRef = useRef(null);
  const flow = useAnalysisFlow();

  const selectedRow =
    rows.find((row) => (row.adset_id || row.adset_name) === selectedKey) || rows[0];
  const rowAssessment =
    selectedRow?.adset_assessment ||
    selectedRow?.adset_assessment_body ||
    null;
  const adsetAssessments = matrix?.adset_assessments || {};
  const persistedAssessment =
    adsetAssessments[selectedRow?.adset_id] ||
    adsetAssessments[selectedRow?.adset_name] ||
    null;
  const selectedAssessment = rowAssessment || fetchedAssessment || persistedAssessment || null;
  const { primaryText: titleColor, secondaryText: tickColor } = getThemeChartColors();
  const selectedRowKey = selectedRow?.adset_id || selectedRow?.adset_name || null;

  useEffect(() => {
    if (typeof onSelectedAdsetChange !== 'function') return;
    if (lastEmittedSelectedAdsetRef.current === selectedRowKey) return;
    lastEmittedSelectedAdsetRef.current = selectedRowKey;
    onSelectedAdsetChange(selectedRow || null);
  }, [onSelectedAdsetChange, selectedRow, selectedRowKey]);

  // Reset flow log when a different adset is selected
  useEffect(() => {
    flow.reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowKey]);

  // One dataset per adset so each bubble gets independent color + selected highlight
  const chartData = {
    datasets: points.map((point) => {
      const isSelected = (point.row.adset_id || point.row.adset_name) === (selectedRow?.adset_id || selectedRow?.adset_name);
      return {
        label: point.label,
        data: [point],
        backgroundColor: point.color,
        borderColor: isSelected ? '#ffffff' : 'transparent',
        borderWidth: isSelected ? 3 : 0,
        radius: (context) => context.raw?.r || 10,
        hoverBorderWidth: 2,
        hoverBorderColor: '#ffffff',
        hoverRadius: 0,
        hitRadius: 14,
        pointStyle: 'circle',
        clip: false,
      };
    }),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    normalized: true,
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: '3) Audience Efficiency Matrix',
        color: titleColor,
        font: { size: 18, weight: 'bold' },
      },
      tooltip: {
        backgroundColor: '#111827',
        borderColor: '#374151',
        borderWidth: 1,
        titleColor: '#f9fafb',
        bodyColor: '#f9fafb',
        displayColors: false,
        padding: 12,
        cornerRadius: 10,
        caretPadding: 8,
        xAlign: 'center',
        yAlign: 'top',
        callbacks: {
          title(context) {
            return context?.[0]?.raw?.row?.adset_name || context?.[0]?.dataset?.label || 'Ad Set';
          },
          label(context) {
            const point = context.raw || {};
            const row = point.row || {};
            return [
              `Audience Type: ${formatAudienceType(row.audience_type)}`,
              `Efficiency: ${point.x ?? 0}`,
              `Scale: ${point.y ?? 0}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        title: { display: true, text: 'Efficiency Quality', color: titleColor, font: { size: 14, weight: 'bold' } },
        grid: { display: false },
        ticks: { color: tickColor, stepSize: 20 },
      },
      y: {
        min: 0,
        max: 100,
        title: { display: true, text: 'Scale / Spend Share', color: titleColor, font: { size: 14, weight: 'bold' } },
        grid: { display: false },
        ticks: {
          color: tickColor,
          stepSize: 20,
          callback: (value) => `${value}`,
        },
      },
    },
    layout: { padding: 20 },
    onClick: (_, elements) => {
      if (!elements?.length) return;
      const { datasetIndex, index } = elements[0];
      const clickedPoint = chartData.datasets[datasetIndex]?.data?.[index];
      if (clickedPoint?.row) {
        setSelectedKey(clickedPoint.row.adset_id || clickedPoint.row.adset_name || null);
      }
    },
  };

  const detailFields = [
    { label: 'Campaign', value: selectedRow.campaign_name || selectedRow.campaign_id },
    { label: 'Audience Type', value: formatAudienceType(selectedRow.audience_type) },
    { label: 'Performance Confidence', value: selectedRow.performance_confidence || '—' },
    { label: 'Geo Targeting', value: selectedRow.geo_targeting },
    // { label: 'Country', value: selectedRow.country }, // redundant — same value shown in Geo Targeting above
    { label: 'City', value: formatDetailValue(selectedRow.city) },
    { label: 'Age Range', value: selectedRow.age_range },
    { label: 'Gender', value: selectedRow.gender },
    { label: 'Lookalike Ratio', value: selectedRow.lookalike_ratio },
    { label: 'Spend', value: currency ? `${currency} ${fmt(selectedRow.spend)}` : fmt(selectedRow.spend) },
    { label: 'Revenue', value: currency ? `${currency} ${fmt(selectedRow.revenue)}` : fmt(selectedRow.revenue) },
    { label: 'ROAS', value: selectedRow.roas != null ? `${fmt(selectedRow.roas)}×` : '—' },
    { label: 'CAC', value: selectedRow.cac != null ? (currency ? `${currency} ${fmt(selectedRow.cac)}` : fmt(selectedRow.cac)) : '—' },
    { label: 'CTR', value: selectedRow.ctr != null ? `${fmt(selectedRow.ctr)}%` : '—' },
    { label: 'ATC Rate', value: selectedRow.add_to_cart_rate_from_clicks != null ? `${fmt(selectedRow.add_to_cart_rate_from_clicks)}%` : '—' },
    { label: 'Checkout Rate', value: selectedRow.checkout_rate_from_clicks != null ? `${fmt(selectedRow.checkout_rate_from_clicks)}%` : '—' },
    { label: 'Purchase Rate', value: selectedRow.purchase_rate_from_clicks != null ? `${fmt(selectedRow.purchase_rate_from_clicks)}%` : '—' },
    { label: 'Contribution After Marketing', value: currency ? `${currency} ${fmt(selectedRow.contribution_after_marketing)}` : fmt(selectedRow.contribution_after_marketing) },
  ];

  useEffect(() => {
    let cancelled = false;
    setAnalysisError('');

    if (!selectedRow) {
      setFetchedAssessment(null);
      setIsAssessmentLoading(false);
      return undefined;
    }

    if (rowAssessment || persistedAssessment) {
      setFetchedAssessment(rowAssessment || persistedAssessment);
      setIsAssessmentLoading(false);
      return undefined;
    }

    if (!userId || !reportId) {
      setFetchedAssessment(null);
      setIsAssessmentLoading(false);
      return undefined;
    }

    setFetchedAssessment(null);
    setIsAssessmentLoading(true);

    getShopMetaAdsetAnalysis({
      userId,
      reportId,
      shopId,
      adsetId: selectedRow.adset_id,
      adsetName: selectedRow.adset_name,
      authToken,
    })
      .then((result) => {
        if (cancelled) return;
        setFetchedAssessment(result?.assessment || null);
      })
      .catch(() => {
        if (cancelled) return;
        setFetchedAssessment(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsAssessmentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    authToken,
    persistedAssessment,
    reportId,
    rowAssessment,
    selectedRow,
    selectedRow?.adset_id,
    selectedRow?.adset_name,
    shopId,
    userId,
  ]);

  if (!rows.length) return null;

  const handleAnalyze = async () => {
    if (!userId || !selectedRow) return;
    setIsAnalyzing(true);
    setAnalysisError('');
    flow.start();
    try {
      const result = await analyzeShopMetaAdset({
        userId,
        reportId,
        shopId,
        adsetId: selectedRow.adset_id,
        adsetName: selectedRow.adset_name,
        authToken,
      });
      flow.resolve();
      if (result?.adset_key && result?.assessment && typeof onAssessmentSaved === 'function') {
        setFetchedAssessment(result.assessment);
        onAssessmentSaved(result.adset_key, result.assessment);
      }
    } catch (error) {
      flow.fail(error?.message || 'Failed to analyze the selected ad set.');
      setAnalysisError(error?.message || 'Failed to analyze the selected ad set.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleShowCampaignPerformance = () => {
    if (!selectedRow || typeof onShowCampaignPerformance !== 'function') return;
    onShowCampaignPerformance({
      campaignId: selectedRow.campaign_id || '',
      campaignName: selectedRow.campaign_name || '',
      adsetId: selectedRow.adset_id || '',
      adsetName: selectedRow.adset_name || '',
    });
  };

  const contextEntries = buildKeyValueEntries(selectedAssessment?.adset_context);
  const labelEntries = buildKeyValueEntries(selectedAssessment?.labels);
  const metricEntries = buildKeyValueEntries(selectedAssessment?.computed_metrics);
  const ruleEntries = buildKeyValueEntries(selectedAssessment?.future_campaign_rules);
  const exactReasons = Array.isArray(selectedAssessment?.exact_reasons)
    ? selectedAssessment.exact_reasons.filter(Boolean)
    : [];
  const shouldShowAnalyzeButton = !selectedAssessment;

  return (
    <Section title="Audience Efficiency Matrix">
      <div className={styles.matrixShell}>
        <div className={styles.matrixSelectorCard}>
          <div className={styles.matrixSelectorHeader}>
            <p className={styles.matrixEyebrow}>Available Ad Sets</p>
            <span className={styles.matrixSelectorCount}>{rows.length} total</span>
          </div>
          <div className={styles.matrixSelectorList}>
            {rows.map((row) => {
              const key = row.adset_id || row.adset_name || '';
              const isActive = key === (selectedRow.adset_id || selectedRow.adset_name);
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.matrixSelectorItem} ${isActive ? styles.matrixSelectorItemActive : ''}`}
                  onClick={() => setSelectedKey(key)}
                >
                  <span className={styles.matrixSelectorName}>{row.adset_name || row.adset_id || 'Ad Set'}</span>
                  <span className={styles.matrixSelectorMeta}>
                    {currency ? `${currency} ${fmt(row.spend)}` : fmt(row.spend)} spend · {row.roas != null ? `${fmt(row.roas)}× ROAS` : '—'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.matrixChartCard}>
          <div className={styles.matrixChartWrap}>
            <Bubble
              data={chartData}
              options={chartOptions}
              plugins={[audienceMatrixQuadrantPlugin, audienceMatrixBubbleLabelPlugin]}
            />
          </div>
        </div>

        <LeakWaterfallChart row={selectedRow} />

        <div className={styles.matrixDetailCard}>
          <div className={styles.matrixDetailHeader}>
            <div>
              <p className={styles.matrixEyebrow}>Selected Ad Set</p>
              <h4 className={styles.matrixDetailTitle}>{selectedRow.adset_name || selectedRow.adset_id || 'Ad Set'}</h4>
            </div>
            {selectedRow.performance_confidence && (
              <span className={styles.matrixConfidenceBadge}>{selectedRow.performance_confidence}</span>
            )}
          </div>
          <div className={styles.matrixActionRow}>
            {/* <button
              type="button"
              className={styles.matrixSecondaryButton}
              onClick={handleShowCampaignPerformance}
            >
              Show raw adset data
            </button> */}
            {(isAssessmentLoading || shouldShowAnalyzeButton) ? (
              <button
                type="button"
                className={`${styles.matrixAnalyzeButton} ${(isAnalyzing || isAssessmentLoading) ? styles.matrixAnalyzeButtonBusy : ''}`}
                onClick={handleAnalyze}
                disabled={isAnalyzing || isAssessmentLoading || !userId}
              >
                {isAssessmentLoading ? 'Checking saved analysis…' : isAnalyzing ? 'Analyzing…' : 'Analyze Ad Set'}
              </button>
            ) : null}
            {analysisError ? (
              <span className={styles.matrixActionError}>{analysisError}</span>
            ) : null}
          </div>
          <div className={styles.perfFields}>
            {detailFields.map((field) => (
              <PerfField key={field.label} label={field.label} value={field.value} />
            ))}
          </div>
        </div>

        <AnalysisFlowLog steps={flow.steps} label="Ad Set Analysis Progress" />

        {!isAssessmentLoading && selectedAssessment ? (
          <div className={styles.matrixAssessmentCard}>
            <div className={styles.matrixAssessmentHeader}>
              <p className={styles.matrixEyebrow}>Ad Set Analysis</p>
              <span className={styles.matrixAssessmentLabel}>
                {selectedAssessment?.labels?.overall_justification || 'Analyzed'}
              </span>
            </div>
            <div className={styles.matrixAssessmentInner}>
              {contextEntries.length ? (
                <div className={styles.matrixAssessmentSection}>
                  <span className={styles.matrixAssessmentSectionTitle}>Ad Set Context</span>
                  <div className={styles.matrixAssessmentGrid}>
                    {contextEntries.map(([key, value]) => (
                      <div key={key} className={styles.matrixAssessmentMetric}>
                        <span className={styles.matrixAssessmentMetricLabel}>{formatAnalysisKey(key)}</span>
                        <p className={styles.matrixAssessmentText}>{formatAnalysisValue(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {selectedAssessment.executive_finding ? (
                <div className={styles.matrixAssessmentSection}>
                  <span className={styles.matrixAssessmentSectionTitle}>Executive Finding</span>
                  <p className={styles.matrixAssessmentBody}>{selectedAssessment.executive_finding}</p>
                </div>
              ) : null}
              {labelEntries.length ? (
                <div className={styles.matrixAssessmentSection}>
                  <span className={styles.matrixAssessmentSectionTitle}>Labels</span>
                  <div className={styles.matrixAssessmentGrid}>
                    {labelEntries.map(([key, value]) => (
                      <div key={key} className={styles.matrixAssessmentMetric}>
                        <span className={styles.matrixAssessmentMetricLabel}>{formatAnalysisKey(key)}</span>
                        <p className={styles.matrixAssessmentText}>{formatAnalysisValue(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {metricEntries.length ? (
                <div className={styles.matrixAssessmentSection}>
                  <span className={styles.matrixAssessmentSectionTitle}>Computed Metrics</span>
                  <div className={styles.matrixAssessmentGrid}>
                    {metricEntries.map(([key, value]) => (
                      <div key={key} className={styles.matrixAssessmentMetric}>
                        <span className={styles.matrixAssessmentMetricLabel}>{formatAnalysisKey(key)}</span>
                        <p className={styles.matrixAssessmentText}>{formatAnalysisValue(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {ruleEntries.length ? (
                <div className={styles.matrixAssessmentSection}>
                  <span className={styles.matrixAssessmentSectionTitle}>Future Campaign Rules</span>
                  <div className={styles.matrixAssessmentGrid}>
                    {ruleEntries.map(([key, value]) => (
                      <div key={key} className={styles.matrixAssessmentMetric}>
                        <span className={styles.matrixAssessmentMetricLabel}>{formatAnalysisKey(key)}</span>
                        <p className={styles.matrixAssessmentText}>{formatAnalysisValue(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {selectedAssessment?.ad_explanation ? (
                <div className={styles.matrixAssessmentSection}>
                  <span className={styles.matrixAssessmentSectionTitle}>Ad Explanation</span>
                  <p className={styles.matrixAssessmentText}>{selectedAssessment.ad_explanation}</p>
                </div>
              ) : null}
              {exactReasons.length ? (
                <div className={styles.matrixAssessmentSection}>
                  <span className={styles.matrixAssessmentSectionTitle}>Exact Reasons</span>
                  <ul className={styles.matrixAssessmentList}>
                    {exactReasons.map((reason, index) => (
                      <li key={`${index}-${reason}`} className={styles.matrixAssessmentListItem}>
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </Section>
  );
};

export default AudienceEfficiencyMatrixSection;
